import { useState, useEffect, useCallback } from 'react';
import { gapi } from 'gapi-script';

const CLIENT_ID = '496815410411-2900abpl3en9e6lca1kgk1jl1bbnrinn.apps.googleusercontent.com';

export const useAuth = () => {
  const [user, setUser] = useState<{ email: string } | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));

  const isTokenExpired = (token: string | null): boolean => {
    if (!token) return true;

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp && payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  };

  // Force token refresh by initiating a new sign-in flow
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      if (!gapi.auth2) {
        await new Promise<void>((resolve) => {
          gapi.load('auth2', resolve);
        });
        await gapi.auth2.init({ client_id: CLIENT_ID });
      }

      const auth = gapi.auth2.getAuthInstance() as any;
      // If not signed in, we can't refresh the token
      if (!auth.isSignedIn.get()) {
        return false;
      }

      // Get the current user
      const currentUser = auth.currentUser.get();
      
      // Force token refresh (this uses Google's silent refresh)
      const authResponse = await currentUser.reloadAuthResponse();
      const newToken = authResponse.id_token;
      
      if (newToken) {
        // Update the token in state and localStorage
        setToken(newToken);
        localStorage.setItem('token', newToken);
        
        // Notify any listeners that the token has been refreshed
        window.dispatchEvent(new CustomEvent('token:refreshed'));
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const initGoogleAuth = async () => {
      await new Promise<void>((resolve) => {
        gapi.load('auth2', resolve);
      });

      const auth = gapi.auth2.init({ client_id: CLIENT_ID });

      (auth.currentUser as any).listen((googleUser: any) => {
        if (googleUser.isSignedIn()) {
          const idToken = googleUser.getAuthResponse().id_token;

          if (!isTokenExpired(idToken)) {
            const profile = googleUser.getBasicProfile();
            const userData = { email: profile.getEmail() };

            setUser(userData);
            setToken(idToken);

            // Persist user and token in localStorage
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('token', idToken);
          } else {
            gapi.auth2.getAuthInstance().signOut();
            setUser(null);
            setToken(null);
            localStorage.removeItem('user');
            localStorage.removeItem('token');
          }
        } else {
          setUser(null);
          setToken(null);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      });
    };

    initGoogleAuth();
    
    // Set up event listener for token refresh requests
    const handleTokenRefreshRequest = () => {
      refreshToken().catch((error: any) => {
        console.error('Token refresh failed:', error);
        // If refresh fails, sign out the user
        signOut();
      });
    };
    
    window.addEventListener('token:refresh', handleTokenRefreshRequest);
    
    return () => {
      window.removeEventListener('token:refresh', handleTokenRefreshRequest);
    };
  }, [refreshToken]);

  const signIn = async () => {
    try {
      // Get the auth instance and sign in
      const authInstance = gapi.auth2.getAuthInstance();
      const googleUser = await authInstance.signIn();
      const idToken = googleUser.getAuthResponse().id_token;
    
      if (!isTokenExpired(idToken)) {
        const profile = googleUser.getBasicProfile();
        const userData = { email: profile.getEmail() };
    
        setUser(userData);
        setToken(idToken);
    
        // Persist user and token in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', idToken);
      }
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const signOut = () => {
    gapi.auth2.getAuthInstance().signOut();
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  return { user, token, signIn, signOut, isTokenExpired, refreshToken };
};